import { useRef, useCallback } from 'react';
import { FabricImage } from 'fabric';
import { useCanvasStore } from '../stores/canvasStore';
import { useStudioStore } from '../stores/studioStore';
import { supabase } from '../lib/supabase';
import { ELEMENT_ID_KEY, ARTBOARD_PADDING } from '../lib/textMeasurement';
import type { ImageElement } from '../types';

export function useImageUpload(opts: {
  artboardRef: React.RefObject<import('fabric').Rect | null>;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageClickPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        e.target.value = '';
        return;
      }

      const canvas = useCanvasStore.getState().fabricCanvas;
      const artboard = opts.artboardRef.current;
      if (!canvas || !artboard) return;

      const artboardLeft = artboard.left ?? ARTBOARD_PADDING;
      const artboardTop = artboard.top ?? ARTBOARD_PADDING;
      const cw = useCanvasStore.getState().canvasWidth;
      const ch = useCanvasStore.getState().canvasHeight;

      const clickPos = imageClickPosRef.current;
      imageClickPosRef.current = null;

      const blobUrl = URL.createObjectURL(file);
      const elementId = crypto.randomUUID();
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const storagePath = `project-images/${elementId}.${ext}`;

      FabricImage.fromURL(blobUrl, { crossOrigin: 'anonymous' }).then(async (img) => {
        const maxW = Math.min(400, cw * 0.6);
        const naturalW = img.width ?? maxW;
        const naturalH = img.height ?? maxW;
        const scale = naturalW > maxW ? maxW / naturalW : 1;
        const scaledW = Math.round(naturalW * scale);
        const scaledH = Math.round(naturalH * scale);

        const placeX = clickPos
          ? Math.round(clickPos.x - scaledW / 2)
          : Math.round((cw - scaledW) / 2);
        const placeY = clickPos
          ? Math.round(clickPos.y - scaledH / 2)
          : Math.round((ch - scaledH) / 2);

        img.set({
          left: artboardLeft + placeX,
          top: artboardTop + placeY,
          scaleX: scale,
          scaleY: scale,
        });

        (img as any)[ELEMENT_ID_KEY] = elementId;
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.requestRenderAll();

        let persistentUrl = blobUrl;
        const { error: uploadError } = await supabase.storage
          .from('cs-assets')
          .upload(storagePath, file, { contentType: file.type, upsert: true });

        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('cs-assets')
            .getPublicUrl(storagePath);
          if (urlData?.publicUrl) {
            persistentUrl = urlData.publicUrl;
          }
        }

        URL.revokeObjectURL(blobUrl);

        const currentElements = useStudioStore.getState().canvasState.elements;
        const newElement: ImageElement = {
          id: elementId,
          type: 'image',
          name: file.name.replace(/\.[^.]+$/, '') || 'Image',
          x: placeX,
          y: placeY,
          width: scaledW,
          height: scaledH,
          rotation: 0,
          visible: true,
          locked: false,
          zIndex: currentElements.length,
          opacity: 1,
          layer: 'BACKGROUND_LAYER',
          contentType: 'STATIC',
          src: persistentUrl,
          alt: '',
          objectFit: 'cover',
        };
        useStudioStore.getState().addElement(newElement);
      }).catch(() => {
        URL.revokeObjectURL(blobUrl);
      });

      e.target.value = '';
      useCanvasStore.getState().setActiveTool('select');
    } catch (err) {
      console.error('[Image Upload] Failed to import image:', err);
      alert(`Failed to import image: ${err instanceof Error ? err.message : 'Unknown error'}`);
      if (e.target) e.target.value = '';
      useCanvasStore.getState().setActiveTool('select');
    }
  }, [opts.artboardRef]);

  return { fileInputRef, imageClickPosRef, handleImageUpload };
}
